/**
 * @component ry-pricing-card
 * @category  display
 * @summary   Pricing tier card — title + price + arbitrary feature list / CTA (ry-ui-only; no Fomantic equivalent). Pair multiple `<ry-pricing-card>`s inside a `<ry-pricing>` container for the classic three-tier pricing table. The `featured` attribute highlights the recommended plan.
 *
 * @attribute title     string?              Plan name (e.g. "Free", "Pro", "Enterprise"). Renders into a leading `<h3>` slot.
 * @attribute price     string?              Price label (e.g. "$0", "$29/mo", "Contact us"). Renders into a price slot below the title.
 * @attribute featured  boolean   default=false   Marks this card as the recommended plan. Pure CSS hook for theme emphasis (border, badge, scale).
 *
 * @content   Free-form children that follow the title + price slots. Typically a feature list and a CTA button. Children are kept as-is — only the title and price are PREPENDED.
 *
 * @dom
 *   ry-pricing-card[title?][price?][featured?]
 *     > h3.ry-pricing-card__title    [data-ry-target="title"]   (only when [title])
 *     > div.ry-pricing-card__price   [data-ry-target="price"]   (only when [price])
 *     > {authored children}
 *
 * @gotcha  Setup-once: changing `title` / `price` attributes after mount does NOT update the rendered slots. Replace the element or re-mount.
 *
 * @gotcha  Children are KEPT and the title/price slots are PREPENDED. If you also include your own `<h3>` inside, you'll get a duplicate heading. Either set the title attribute OR author your own heading, not both.
 *
 * @gotcha  `featured` is a CSS-only hook — the JS doesn't manage z-index or pop-up effects. To pop the featured card out visually, the theme handles it.
 *
 * @example basic
 * ```html
 * <ry-pricing-card title="Free" price="$0">
 *   <ul><li>Up to 5 projects</li><li>Community support</li></ul>
 *   <ry-button>Sign up</ry-button>
 * </ry-pricing-card>
 * ```
 *
 * @example featured
 * ```html
 * <ry-pricing-card title="Pro" price="$29/mo" featured>
 *   <ul><li>Unlimited projects</li><li>Priority support</li><li>SSO</li></ul>
 *   <ry-button variant="primary">Choose Pro</ry-button>
 * </ry-pricing-card>
 * ```
 *
 * @example contact-cta
 * ```html
 * <ry-pricing-card title="Enterprise" price="Contact us">
 *   <ul><li>Custom contracts</li><li>SLA</li></ul>
 *   <ry-button>Talk to sales</ry-button>
 * </ry-pricing-card>
 * ```
 *
 * @example in-pricing-grid
 * ```html
 * <ry-pricing>
 *   <ry-pricing-card title="Free"  price="$0"      ><ry-button>Sign up</ry-button></ry-pricing-card>
 *   <ry-pricing-card title="Pro"   price="$29/mo" featured><ry-button variant="primary">Buy</ry-button></ry-pricing-card>
 *   <ry-pricing-card title="Scale" price="$99/mo" ><ry-button>Buy</ry-button></ry-pricing-card>
 * </ry-pricing>
 * ```
 *
 * @tutorial title-and-price-render-slots
 * ```js
 * const el = mount('<ry-pricing-card title="Pro" price="$29/mo">body</ry-pricing-card>');
 * await expectEqual(el.querySelector('[data-ry-target="title"]')?.textContent, 'Pro');
 * await expectEqual(el.querySelector('[data-ry-target="price"]')?.textContent, '$29/mo');
 * ```
 *
 * @tutorial missing-title-omits-slot
 * ```js
 * const el = mount('<ry-pricing-card price="$0">body</ry-pricing-card>');
 * await expectEqual(el.querySelector('[data-ry-target="title"]'), null);
 * await expectEqual(el.querySelector('[data-ry-target="price"]')?.textContent, '$0');
 * ```
 *
 * @tutorial missing-price-omits-slot
 * ```js
 * const el = mount('<ry-pricing-card title="Custom">body</ry-pricing-card>');
 * await expectEqual(el.querySelector('[data-ry-target="title"]')?.textContent, 'Custom');
 * await expectEqual(el.querySelector('[data-ry-target="price"]'), null);
 * ```
 *
 * @tutorial children-are-preserved
 * ```js
 * // Wrong assumption: children are replaced by the title/price markup.
 * // They are preserved AFTER the title/price slots.
 * const el = mount('<ry-pricing-card title="Pro" price="$29"><p id="body">Features</p></ry-pricing-card>');
 * await expectEqual(!!el.querySelector('#body'), true, 'authored content survives');
 * ```
 *
 * @tutorial featured-attribute-survives
 * ```js
 * const el = mount('<ry-pricing-card title="Pro" price="$29" featured>body</ry-pricing-card>');
 * await expectEqual(el.hasAttribute('featured'), true);
 * ```
 *
 * @tutorial dom-order-title-then-price-then-children
 * ```js
 * const el = mount('<ry-pricing-card title="Pro" price="$29"><p id="body">x</p></ry-pricing-card>');
 * const title = el.querySelector('[data-ry-target="title"]');
 * const price = el.querySelector('[data-ry-target="price"]');
 * const body = el.querySelector('#body');
 * // title precedes price precedes body
 * await expectEqual(
 *   !!(title.compareDocumentPosition(price) & Node.DOCUMENT_POSITION_FOLLOWING),
 *   true,
 *   'title comes before price',
 * );
 * await expectEqual(
 *   !!(price.compareDocumentPosition(body) & Node.DOCUMENT_POSITION_FOLLOWING),
 *   true,
 *   'price comes before body',
 * );
 * ```
 *
 * @see ry-pricing
 * @see ry-card
 * @see ry-feature
 */

/*
 * <ry-pricing> — companion container for <ry-pricing-card>.
 *
 * Pure CSS styling hook — no JS. Lays out child cards in a responsive grid.
 * The docs-compile pipeline only handles one @component per file, so this
 * sibling export is documented in prose and verified by vitest only. See
 * tests/components/ry-pricing.test.ts for the behavior contract.
 */

import { RyElement } from '../core/ry-element.js';

export class RyPricing extends RyElement {}

export class RyPricingCard extends RyElement {
  setup(): void {
    this.#transformMarkup();
  }

  #transformMarkup(): void {
    if (this.$('[data-ry-target="title"]')) return;

    const title = this.getAttribute('title') ?? '';
    const price = this.getAttribute('price') ?? '';
    const childrenHTML = this.innerHTML;

    this.innerHTML = `
      ${title ? `<h3 data-ry-target="title" class="ry-pricing-card__title">${title}</h3>` : ''}
      ${price ? `<div data-ry-target="price" class="ry-pricing-card__price">${price}</div>` : ''}
      ${childrenHTML}
    `;
  }
}

customElements.define('ry-pricing', RyPricing);
customElements.define('ry-pricing-card', RyPricingCard);
